import React, { useMemo, useState } from 'react';
import {
  Modal,
  Table,
  Button,
  Tag,
  Typography,
  Input,
  message,
  Tooltip,
  Popconfirm,
} from 'antd';
import { EyeOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import TransactionService from '../../service/transaction-service/Transaction.service';
import { getSearchCheckboxFilterProps } from '../../utils/table-filter-helper';

const { Text } = Typography;

function formatCardNumber(cardNumber?: string) {
  if (!cardNumber || cardNumber.length < 4) return cardNumber || '';
  return `***${cardNumber.slice(-4)}`;
}

interface WrongPaymentWithdrawalServiceModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  transactions: any[];
  role?: string | null;
}

const WrongPaymentWithdrawalServiceModal: React.FC<
  WrongPaymentWithdrawalServiceModalProps
> = ({ open, onClose, loading, transactions, role }) => {
  const navigate = useNavigate();
  const [localData, setLocalData] = useState<any[]>(transactions);

  React.useEffect(() => {
    setLocalData(transactions);
  }, [transactions]);

  const handleCheckPayment = async (record: any) => {
    try {
      await TransactionService.checkPaymentWithdrawalService(
        record.transaction?.id
      );
      setLocalData((prev) =>
        prev.filter((item) => item.transaction?.id !== record.transaction?.id)
      );
      message.success('Đã đánh dấu kiểm tra thanh toán!');
    } catch (e: any) {
      message.error(e.message || 'Lỗi khi cập nhật trạng thái kiểm tra!');
    }
  };

  // Filter options
  const cardNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          transactions
            .map((t) => t.transaction?.creditCard?.cardNumber)
            .filter(Boolean)
        )
      ).map((n) => ({ text: formatCardNumber(n), value: n })),
    [transactions]
  );

  const customerNames = useMemo(
    () =>
      Array.from(
        new Set(
          transactions
            .map((t) => t.transaction?.creditCard?.customer?.name)
            .filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [transactions]
  );

  const cardHolderNames = useMemo(
    () =>
      Array.from(
        new Set(
          transactions
            .map((t) => t.transaction?.creditCard?.cardHolderName)
            .filter(Boolean)
        )
      ).map((n) => ({ text: n, value: n })),
    [transactions]
  );

  const employeeOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        transactions.map((t) => t.transaction?.employee?.name).filter(Boolean)
      )
    );
    return names.map((name) => ({
      value: name,
      label: name,
    }));
  }, [transactions]);

  const columns = [
    {
      title: 'Ngày GD',
      dataIndex: ['transaction', 'transactionDate'],
      key: 'transactionDate',
      render: (d: string) => (
        <Tag color="blue">{dayjs(d).format('DD-MM-YYYY')}</Tag>
      ),
      sorter: (a: any, b: any) =>
        dayjs(a.transaction?.transactionDate).unix() -
        dayjs(b.transaction?.transactionDate).unix(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm ngày (dd-mm-yyyy)"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 180, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        dayjs(record.transaction?.transactionDate)
          .format('DD-MM-YYYY')
          .includes(value),
    },
    {
      title: 'Số thẻ',
      dataIndex: ['transaction', 'creditCard', 'cardNumber'],
      key: 'cardNumber',
      filters: cardNumbers,
      onFilter: (value: any, record: any) =>
        record.transaction?.creditCard?.cardNumber === value,
      render: (_: any, r: any) =>
        formatCardNumber(r.transaction?.creditCard?.cardNumber),
    },
    {
      title: 'Chủ thẻ',
      dataIndex: ['transaction', 'creditCard', 'cardHolderName'],
      key: 'cardHolderName',
      filters: cardHolderNames,
      filterSearch: true,
      onFilter: (value: any, record: any) =>
        record.transaction?.creditCard?.cardHolderName === value,
      render: (_: any, r: any) => r.transaction?.creditCard?.cardHolderName,
    },
    {
      title: 'Khách hàng',
      dataIndex: ['transaction', 'creditCard', 'customer', 'name'],
      key: 'customer',
      filters: customerNames,
      filterSearch: true,
      onFilter: (value: any, record: any) =>
        record.transaction?.creditCard?.customer?.name === value,
      render: (_: any, r: any) => r.transaction?.creditCard?.customer?.name,
    },
    {
      title: 'Số rút',
      dataIndex: ['transaction', 'amount'],
      key: 'amount',
      sorter: (a: any, b: any) =>
        (a.transaction?.amount || 0) - (b.transaction?.amount || 0),
      render: (v: number) => v?.toLocaleString(),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm số rút"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.transaction?.amount || '').toString().includes(value),
    },
    {
      title: 'Phí KH',
      key: 'feeForCustomer',
      render: (_: any, record: any) => {
        const fee = record.transaction?.feeForCustomer;
        const type = record.transaction?.customerType;
        if (!fee) return <span className="text-gray-400">0</span>;
        if (type === 'vn') {
          return <span className="text-blue-500">{fee?.toLocaleString()}</span>;
        } else {
          return <span className="text-orange-500">{fee}%</span>;
        }
      },
      sorter: (a: any, b: any) =>
        (a.transaction?.feeForCustomer || 0) -
        (b.transaction?.feeForCustomer || 0),
    },
    {
      title: 'Sau phí (phải trả)',
      dataIndex: 'afterFee',
      key: 'afterFee',
      render: (v: number) => (
        <span className="font-semibold text-green-600">
          {v?.toLocaleString()}
        </span>
      ),
      sorter: (a: any, b: any) => (a.afterFee || 0) - (b.afterFee || 0),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm sau phí"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.afterFee || '').toString().includes(value),
    },
    {
      title: 'Đã trả',
      dataIndex: 'totalPaidToCustomer',
      key: 'totalPaidToCustomer',
      render: (v: number) => (
        <span className="font-semibold text-purple-600">
          {v?.toLocaleString()}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a.totalPaidToCustomer || 0) - (b.totalPaidToCustomer || 0),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm đã trả"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.totalPaidToCustomer || '').toString().includes(value),
    },
    {
      title: 'Chênh lệch',
      dataIndex: 'paymentDifference',
      key: 'paymentDifference',
      sorter: (a: any, b: any) =>
        Math.abs(a.paymentDifference || 0) - Math.abs(b.paymentDifference || 0),
      render: (v: number) => {
        const color = v > 0 ? 'text-red-600' : 'text-orange-600';
        const label = v > 0 ? 'Thừa' : 'Thiếu';
        return (
          <span className={`font-semibold ${color}`}>
            {label}: {Math.abs(v).toLocaleString()}
          </span>
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Tìm chênh lệch"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={confirm}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={confirm}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Lọc
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Xóa lọc
          </Button>
        </div>
      ),
      onFilter: (value: any, record: any) =>
        (record.paymentDifference || '').toString().includes(value),
    },
    {
      title: 'Nhân viên',
      dataIndex: ['transaction', 'employee', 'name'],
      key: 'employee',
      ...getSearchCheckboxFilterProps('Nhân viên', employeeOptions),
      onFilter: (value: any, record: any) =>
        record.transaction?.employee?.name === value,
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: 'Hành động',
      key: 'action',
      fixed: 'right' as const,
      width: 150,
      render: (_: any, record: any) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Xem chi tiết giao dịch">
            <Button
              icon={<EyeOutlined />}
              type="link"
              size="small"
              onClick={() => {
                // Navigate to withdrawal service page with filters
                navigate('/withdrawal-service');
              }}
            />
          </Tooltip>
          {/* Nút check - chỉ với admin/manager */}
          {role !== 'ROLE_EMPLOYEE' && (
            <Popconfirm
              title="Đánh dấu đã kiểm tra thanh toán?"
              description="Giao dịch sẽ bị ẩn khỏi danh sách này sau khi đánh dấu."
              onConfirm={() => handleCheckPayment(record)}
              okText="Đã kiểm tra"
              cancelText="Hủy"
            >
              <Tooltip title="Đã kiểm tra">
                <Button
                  icon={<CheckOutlined />}
                  type="primary"
                  ghost
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const modalBodyRef = React.useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span style={{ fontWeight: 600, fontSize: 18 }}>
          Dịch vụ rút tiền - Trả tiền sai cho khách (≥51K)
        </span>
      }
      footer={null}
      width={1400}
      bodyStyle={{ padding: 24 }}
    >
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
        <p className="text-yellow-800 text-sm mb-1">
          <strong>Giải thích:</strong>
        </p>
        <p className="text-yellow-700 text-xs">
          • <strong>Sau phí (phải trả)</strong>: Số tiền chuẩn phải trả cho
          khách = Số rút - Phí KH
          <br />• <strong>Đã trả</strong>: Tổng số tiền thực tế đã trả cho khách
          <br />• <strong>Chênh lệch</strong>: Đã trả - Phải trả
          <br />
          &nbsp;&nbsp;→ <span className="text-red-600">Thừa</span>: Trả nhiều
          hơn cần thiết
          <br />
          &nbsp;&nbsp;→ <span className="text-orange-600">Thiếu</span>: Trả ít
          hơn cần thiết
        </p>
      </div>
      <div
        ref={modalBodyRef}
        style={{ maxHeight: 600, overflowY: 'auto', position: 'relative' }}
      >
        <Table
          columns={columns}
          dataSource={localData}
          loading={loading}
          rowKey={(r) => r.transaction?.id}
          scroll={{ x: 1400 }}
          sticky={{ offsetHeader: 0, getContainer: () => modalBodyRef.current! }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          bordered
          size="middle"
          locale={{ emptyText: 'Không có giao dịch nào.' }}
        />
      </div>
    </Modal>
  );
};

export default WrongPaymentWithdrawalServiceModal;
